import { ref, computed, watch, onBeforeUnmount, Ref } from 'vue';
import { useElementBounding } from '@vueuse/core';
import eventBus from '@ve/services/eventBus';

interface AnchorObserver {
    observe: (container: HTMLElement) => void;
    disconnect: () => void;
}

interface AnchorObserverContext {
    el: HTMLElement;
    leadBannerAnchor?: AnchorObserver | null;
}

export function createAnchorObserver(channel: string, selector: string, context: AnchorObserverContext): AnchorObserver {
    let observer: IntersectionObserver | null = null;

    context.leadBannerAnchor?.disconnect();

    const instance: AnchorObserver = {
        observe(container: HTMLElement) {
            this.disconnect();
            const target = container.querySelector<HTMLElement>(selector);
            if (!target) return;

            observer = new IntersectionObserver((entries) => {
                const entry = entries[0];
                if (entry?.isIntersecting) {
                    eventBus.$emit(`portal-anchor:${channel}`, entry.target);
                }
            });
            observer.observe(target);
        },
        disconnect() {
            observer?.disconnect();
            observer = null;
        },
    };

    instance.observe(context.el);

    return instance;
}

interface PortalAnchorReturn {
    anchorPosition: Ref<{ top: string; left: string } | null>;
    resetAnchor: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default function usePortalAnchor(
    channel: string,
    floatingComponentRef: Ref<any>,
): PortalAnchorReturn {
    const anchorEl = ref<HTMLElement | null>(null);
    const anchorPosition = ref<{ top: string; left: string } | null>(null);

    const floatingEl = computed(() => floatingComponentRef.value?.$el as HTMLElement | undefined);
    const { height: floatingHeight } = useElementBounding(floatingEl);

    const eventName = `portal-anchor:${channel}`;

    const onAnchorVisible = (el: HTMLElement) => {
        anchorPosition.value = {
            top: `${el.offsetTop}px`,
            left: `${el.offsetLeft}px`,
        };
        anchorEl.value = el;
    };

    eventBus.$on(eventName, onAnchorVisible);

    const resetAnchor = () => {
        anchorPosition.value = null;
        anchorEl.value = null;
    };

    watch([floatingHeight, anchorEl], ([height, anchor]) => {
        if (height && anchor) {
            anchor.style.height = `${height}px`;
        }
    });

    onBeforeUnmount(() => {
        eventBus.$off(eventName, onAnchorVisible);
    });

    return {
        anchorPosition,
        resetAnchor,
    };
}
